import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Shield, ChevronLeft, Mail, FileText, CheckCircle } from 'lucide-react';

const SecurityPage = () => {
  const scope = [
    "getsortio.com (marketing site)",
    "app.getsortio.com (web app + checkout)",
    "api.getsortio.com (backend API)",
    "The Sortio desktop application (macOS, Windows)",
  ];

  const outOfScope = [
    "Denial of service or volumetric attacks",
    "Social engineering of CMG Labs employees or users",
    "Issues that require physical access to a user's device",
    "Findings limited to outdated browsers or end of life software",
    "Reports from automated scanners without a working proof of concept",
  ];

  return (
    <div className="min-h-screen bg-black text-apple-100 py-24">
      <Helmet>
        <title>Security and Vulnerability Disclosure - Sortio</title>
        <meta name="description" content="How to report security vulnerabilities to Sortio (CMG Labs). Coordinated disclosure policy and security contact." />
      </Helmet>

      <div className="absolute top-0 inset-x-0 h-[500px] bg-gradient-to-b from-apple-700/10 to-transparent"></div>
      <div className="absolute -top-[600px] -right-[300px] w-[1000px] h-[1000px] bg-sortio-primary/5 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-6 max-w-4xl relative z-10">
        <Link to="/" className="inline-flex items-center text-apple-300 hover:text-sortio-primary mb-12 transition-colors group">
          <ChevronLeft className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform" />
          <span>Back to Home</span>
        </Link>

        <div className="mb-16 flex flex-col items-center">
          <div className="w-14 h-14 rounded-full bg-apple-700/60 backdrop-blur-sm flex items-center justify-center mb-6 border border-apple-600/30">
            <Shield className="w-6 h-6 text-sortio-primary" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-center">
            Security and <span className="apple-gradient-text">Vulnerability Disclosure</span>
          </h1>
          <p className="text-apple-300 mt-4 text-center max-w-xl">
            We welcome reports from security researchers. Here is how to reach us.
          </p>
        </div>

        <div className="apple-panel p-8 md:p-12 rounded-apple backdrop-blur-sm mb-12">
          <div className="space-y-8">
            <section className="space-y-4">
              <h2 className="text-xl font-medium text-apple-50">How Sortio protects your files</h2>
              <p className="text-apple-300 text-sm leading-relaxed">
                Sortio organizes files on your own machine. For teams in regulated work such as law
                firms, accounting practices, and healthcare, here is exactly what happens to a
                document when Sortio sorts it.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-sortio-secondary mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-apple-300">
                    <span className="text-apple-100 font-medium">Local-only option.</span> With local
                    inference through Ollama, the model runs on your device and no file content ever
                    leaves the machine. This is the configuration for privileged, confidential, or
                    regulated documents.
                  </span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-sortio-secondary mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-apple-300">
                    <span className="text-apple-100 font-medium">Managed AI or your own key.</span>{' '}
                    Prefer hosted AI for speed, or bring your own provider API key (BYOK) so calls go
                    to an account you control.
                  </span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-sortio-secondary mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-apple-300">
                    <span className="text-apple-100 font-medium">Preview before anything moves.</span>{' '}
                    Every rename and move is shown for approval first. Nothing is destructive until
                    you apply, and Sortio keeps backup archives of your most recent sorts so a sort
                    can be undone from inside the app.
                  </span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-sortio-secondary mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-apple-300">
                    <span className="text-apple-100 font-medium">Your filesystem, not a silo.</span>{' '}
                    Sortio organizes the files already on your disk or shared drive into ordinary
                    folders. It does not lock your documents inside a proprietary store.
                  </span>
                </li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-medium text-apple-50">How to report</h2>
              <div className="p-6 rounded-lg bg-sortio-primary/10 border border-sortio-primary/30">
                <div className="flex items-start">
                  <Mail className="w-6 h-6 text-sortio-primary mr-4 mt-1 flex-shrink-0" />
                  <div className="space-y-2">
                    <p className="text-apple-100 font-medium">Email our security team</p>
                    <p className="text-apple-300 text-sm">
                      Send a detailed report to{' '}
                      <a
                        href="mailto:security@getsortio.com"
                        className="text-sortio-primary hover:text-sortio-primary/80 font-medium"
                      >
                        security@getsortio.com
                      </a>
                      .
                    </p>
                    <p className="text-apple-300 text-sm">
                      Please include reproduction steps, affected URLs or app versions, and any proof of concept. PGP is not required.
                    </p>
                  </div>
                </div>
              </div>
              <p className="text-apple-300 text-sm leading-relaxed">
                A machine readable version of this policy is published at{' '}
                <a
                  href="/.well-known/security.txt"
                  className="text-sortio-primary hover:text-sortio-primary/80"
                >
                  /.well-known/security.txt
                </a>
                .
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-medium text-apple-50">Our commitments</h2>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-sortio-secondary mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-apple-300">We will acknowledge your report within 3 business days.</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-sortio-secondary mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-apple-300">We will provide a triage decision (in scope, severity, planned fix) within 10 business days.</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-sortio-secondary mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-apple-300">We will not pursue legal action against researchers who follow this policy in good faith.</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-sortio-secondary mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-apple-300">We will credit you in our release notes if you wish, after the fix is shipped.</span>
                </li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-medium text-apple-50">Remediation timelines</h2>
              <p className="text-apple-300 leading-relaxed">
                We aim to resolve confirmed issues on the following schedule, measured from the date we accept the report:
              </p>
              <ul className="space-y-2 text-apple-300">
                <li><strong className="text-apple-50">High severity:</strong> 7 days</li>
                <li><strong className="text-apple-50">Medium severity:</strong> 30 days</li>
                <li><strong className="text-apple-50">Low severity:</strong> 90 days</li>
              </ul>
              <p className="text-apple-300 text-sm leading-relaxed">
                Severity follows CVSS 3.1. Complex issues that require architectural changes may take longer; we will keep you posted on progress.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-medium text-apple-50">In scope</h2>
              <ul className="space-y-2">
                {scope.map((item, i) => (
                  <li key={i} className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-sortio-secondary mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-apple-300">{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-medium text-apple-50">Out of scope</h2>
              <ul className="space-y-2 text-apple-300">
                {outOfScope.map((item, i) => (
                  <li key={i} className="flex items-start">
                    <span className="mr-3 text-apple-400">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-medium text-apple-50">Researcher guidelines</h2>
              <p className="text-apple-300 leading-relaxed">
                While testing, please avoid accessing or modifying data that does not belong to you, do not run automated scanners against production endpoints, do not exfiltrate data beyond the minimum needed to demonstrate the issue, and give us a reasonable opportunity to remediate before any public disclosure.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-medium text-apple-50">Related policies</h2>
              <div className="flex flex-col gap-2 text-apple-300">
                <Link to="/privacy-policy" className="inline-flex items-center text-sortio-primary hover:text-sortio-primary/80">
                  <FileText className="w-4 h-4 mr-2" />
                  Privacy Policy
                </Link>
                <Link to="/sub-processors" className="inline-flex items-center text-sortio-primary hover:text-sortio-primary/80">
                  <FileText className="w-4 h-4 mr-2" />
                  Sub-processors
                </Link>
                <Link to="/terms-and-conditions" className="inline-flex items-center text-sortio-primary hover:text-sortio-primary/80">
                  <FileText className="w-4 h-4 mr-2" />
                  Terms of Service
                </Link>
              </div>
            </section>
          </div>
        </div>

        <div className="flex justify-center mb-8">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="inline-flex items-center px-4 py-2 rounded-full bg-apple-700/40 border border-apple-600/30 text-sm text-apple-300 hover:text-sortio-primary transition-colors"
          >
            <svg className="w-4 h-4 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m18 15-6-6-6 6"/>
            </svg>
            <span>Back to top</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SecurityPage;
